import { A } from '@adornis/base/env-info.js';
import type { Dictionary, Maybe } from '@adornis/base/utilTypes.js';
import { Float, ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getCollection, getCollectionHandle, getRawCollection } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import { selectionSet, type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import {
  exportLearningDataMappingWithFilter,
  exportLearningDataWithFilter,
} from '@chancenwerk/campus-learning-content/api/exportLearningData.js';
import { CampusExercise } from '@chancenwerk/campus-learning-content/entities/CampusExercise.js';
import { CampusLesson } from '@chancenwerk/campus-learning-content/entities/CampusLesson.js';
import { ExerciseType } from '@chancenwerk/db/enums/enums.js';
import { ChancencornerLessonFinishedEventlog } from '@chancenwerk/db/eventlogs/ChancencornerLessonFinishedEventlog.js';
import { ContentResultEventlog } from '@chancenwerk/db/eventlogs/ContentResultEventlog.js';
import { XAPIEventlog } from '@chancenwerk/db/eventlogs/XAPIEventlog.js';
import { CampusLessonFilter } from '@chancenwerk/db/filter/CampusLessonFilter.js';
import type { ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { map, switchMap } from 'rxjs';
import { ChancenEventlog } from '../../db/eventlogs/ChancenEventlog.js';
import { CampusArea, CampusSection } from '../entities/CampusSection.js';
import { LearningContentAPI } from './LearningContentAPI.js';

const resolveGetExercisesWithAnswerForLessonAndUser = (lessonID: string, userID: string) => {
  return async (gqlFields: BaseQLSelectionSet<CampusExercise>) => {
    const col = await getRawCollection<CampusLesson>(CampusLesson._collectionName);
    return col
      .aggregate([
        { $match: { _id: lessonID } },
        {
          $lookup: {
            from: CampusExercise._collectionName,
            as: 'exercise',
            localField: 'exerciseIDs',
            foreignField: '_id',
          },
        },
        { $unwind: '$exercise' },
        {
          $lookup: {
            from: 'eventlog',
            as: 'answers',
            let: { exerciseID: '$exercise._id' },
            pipeline: [
              {
                $match: {
                  $expr: {
                    $and: [
                      { $eq: ['$_class', ContentResultEventlog._class] },
                      { $eq: ['$userID', userID] },
                      { $eq: ['$contentID', '$$exerciseID'] },
                      { $not: { $in: ['$type', [ExerciseType.LEVUMIGERMAN, ExerciseType.LEVUMIMATH]] } },
                    ],
                  },
                },
              },
              { $sort: { createdAt: 1 } },
            ],
          },
        },
        { $addFields: { 'exercise.answers': '$answers' } },
        { $replaceRoot: { newRoot: '$exercise' } },
      ])
      .toArray() as Promise<CampusExercise[]>;
  };
};

const getExercisesWithAnswerForLessonAndUser = registerQuery({
  type: () => [CampusExercise],
  operationName: 'getExercisesWithAnswerForLessonAndUser',
  params: [
    { type: () => ID, name: 'lessonID' },
    { type: () => ID, name: 'userID' },
  ],
  resolve: resolveGetExercisesWithAnswerForLessonAndUser,
});

@Entity()
export class ExerciseWithLearningDataFilter extends AdornisFilter {
  static override _class = 'ExerciseWithLearningDataFilter';

  @Field(type => ID) studentID: Maybe<string>;
  @Field(type => ID) lessonID: Maybe<string>;

  override getMongoFilter(): Dictionary<any> {
    return {
      _class: ContentResultEventlog._class,
      ...(this.studentID ? { userID: this.studentID } : {}),
      ...(this.lessonID ? { lessonID: this.lessonID } : {}),
    };
  }
}

const resolveGetLessonsForArea = (area: CampusArea) => (gqlFields: BaseQLSelectionSet<CampusLesson>) => {
  return watchQuery<CampusSection>(CampusSection._collectionName, { area }).pipe(
    map(ss => ss.flatMap(s => s.lessonIDs).filter(a => a !== null && a !== undefined)),
    switchMap(lessonIDs => getByID(CampusLesson, lessonIDs)(CampusLesson.allFields)),
    map(a => a.toSorted((a, b) => a.name.localeCompare(b.name))),
  );
};

const subscriptionSubscribeLessonsForArea = registerSubscription({
  operationName: 'subscriptionSubscribeLessonsForArea',
  params: [{ type: () => String, name: 'area' }],
  type: () => [CampusLesson],
  resolve: resolveGetLessonsForArea,
});

const resolveSubscribeLessonsByFilter =
  (_skip: number, _limit: number, filter: CampusLessonFilter) => (gqlFields: BaseQLSelectionSet<CampusLesson>) =>
    watchQuery(CampusLesson._collectionName, {}).pipe(
      switchMap(async () => {
        const lessonIDs = (
          await (await getRawCollection<CampusSection>(CampusSection._collectionName))
            .aggregate<{ _id: string }>([
              ...(filter.sectionArea ? [{ $match: { area: filter.sectionArea } }] : []),
              { $unwind: '$lessonIDs' },
              { $match: { $or: [{ hiddenFromStudents: { $ne: true } }, { hiddenFromStudents: { $exists: false } }] } },
              { $group: { _id: '$lessonIDs' } },
            ])
            .toArray()
        ).map(x => x._id);

        const lessonFilter = await publishFilter(CampusLesson);

        return (await (
          await getCollection(CampusLesson)
        )
          .find(
            {
              ...(lessonFilter ? lessonFilter : {}),
              _id: { $in: lessonIDs },
              ...filter.getMongoFilter(),
              ...(filter.search
                ? { name: { $regex: filter.search.replaceAll(/([^\w])/g, '\\$1'), $options: 'i' } }
                : {}),
            },
            {
              sort: { name: 1 },
            },
          )
          .toArray()) as CampusLesson[];
      }),
    );

const subscriptionSubscribeLessonsByFilter = registerSubscription({
  operationName: 'subscriptionSubscribeLessonsByFilter',
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
    { type: () => CampusLessonFilter, name: 'filter' },
  ],
  type: () => [CampusLesson],
  resolve: resolveSubscribeLessonsByFilter,
});

const resolveSubscribeLessonsWithAnswerForStudent = (
  skip: number,
  limit: number,
  filter: ExerciseWithLearningDataFilter,
) => {
  return (gqlFields: BaseQLSelectionSet<CampusLesson>) => {
    return getCollectionHandle(ContentResultEventlog._collectionName)
      .watchQuery(filter.getMongoFilter(), { projection: { _id: 1 } })
      .pipe(
        switchMap(async eventlogs => {
          const col = await getRawCollection<CampusLesson>(ContentResultEventlog._collectionName);
          let lessons = await (col
            .aggregate([
              { $match: { _id: { $in: eventlogs.map(eventlog => eventlog._id) } } },
              { $sort: { createdAt: 1 } },
              { $lookup: { from: 'campus-lessons', as: 'lesson', foreignField: '_id', localField: 'lessonID' } },
              { $unwind: '$lesson' },
              { $group: { _id: '$lesson._id', lesson: { $last: '$lesson' } } },
              { $replaceRoot: { newRoot: '$lesson' } },
              ...(filter.search
                ? [{ $match: { name: { $regex: filter.search.replaceAll(/([^\w])/g, '\\$1'), $options: 'i' } } }]
                : []),
            ])
            .toArray() as Promise<Array<EntityData<CampusLesson>>>);

          // We will filter out lessons that don't have any answers
          const toFilter: Record<number, boolean> = {};
          for (let i = 0; i < lessons.length; i++) {
            const exerciseWithAnswer = await resolveGetExercisesWithAnswerForLessonAndUser(
              lessons[i]!._id,
              filter.studentID ?? '',
            )({ answers: ContentResultEventlog.allFields });

            if (!exerciseWithAnswer.some(ex => (ex.answers ?? []).length > 0)) {
              toFilter[i] = true;
            }
          }
          lessons = lessons.filter((_, i) => !toFilter[i]);

          return lessons.map(x => constructValue(x) as CampusLesson);
        }),
      );
  };
};

const subscribeLessonsWithAnswerForStudent = registerSubscription({
  type: () => [CampusLesson],
  operationName: 'subscribeLessonsWithAnswerForStudent',
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
    { type: () => ExerciseWithLearningDataFilter, name: 'filter' },
  ],
  resolve: resolveSubscribeLessonsWithAnswerForStudent,
});

const resolveGetCorrectAnswerForLesson = (userID: string, lessonID: string) => {
  return async () => {
    const user = await CurrentUserInfo.getMyself<ChancenwerkUser>()({
      roles: { name: 1, permissions: 1, contexts: 1 },
    });

    if (!user) {
      throw new Error('User not found');
    }

    const colXAPI = await getRawCollection<XAPIEventlog>(XAPIEventlog._collectionName);
    const resXAPI = await colXAPI
      .aggregate([
        {
          $match: {
            _class: XAPIEventlog._class,
            lessonID,
            userID,
            'XApiResult.completion': true,
            'XApiResult.success': true,
          },
        },

        {
          $group: {
            _id: '$contentID',
          },
        },
      ])
      .toArray();

    return resXAPI.length;
  };
};

const getCorrectAnswerForLesson = registerQuery({
  type: () => Int,
  operationName: 'getCorrectAnswerForLesson',
  params: [
    { type: () => String, name: 'userID' },
    { type: () => String, name: 'lessonID' },
  ],
  resolve: resolveGetCorrectAnswerForLesson,
});

const resolveGetPartialScoresForLesson = (userID: string, lessonID: string) => {
  return async () => {
    const user = await CurrentUserInfo.getMyself<ChancenwerkUser>()({
      roles: { name: 1, permissions: 1, contexts: 1 },
    });

    if (!user) {
      throw new Error('User not found');
    }

    const colXAPI = await getRawCollection<XAPIEventlog>(XAPIEventlog._collectionName);
    const resXAPI = (await colXAPI
      .aggregate([
        {
          $match: {
            _class: XAPIEventlog._class,
            lessonID,
            userID,
            'XApiResult.completion': true,
          },
        },
        {
          $sort: {
            createdAt: -1,
          },
        },
        {
          $group: {
            _id: '$contentID',
            entries: {
              $push: '$$ROOT',
            },
          },
        },
      ])
      .toArray()) as Array<{ _id: string; entries: XAPIEventlog }>;

    return resXAPI.map(x => x.entries[0].XApiResult?.score?.scaled as number);
  };
};

const getPartialScoresForLesson = registerQuery({
  type: () => [Float],
  operationName: 'getPartialScoresForLesson',
  params: [
    { type: () => String, name: 'userID' },
    { type: () => String, name: 'lessonID' },
  ],
  resolve: resolveGetPartialScoresForLesson,
});

@Entity()
export class CampusLessonDiagnostic extends AdornisEntity {
  public static override _class = 'CampusLessonDiagnostic';

  static override get allFields() {
    return selectionSet(() => CampusLessonDiagnostic, 2);
  }

  @Field(type => ID, {
    default: v => v ?? A.getGloballyUniqueID(),
  })
  public _id!: string;

  @Field(type => String)
  public userID: Maybe<string>;

  @Field(type => String)
  public lessonID: Maybe<string>;

  @Field(type => String)
  public name: Maybe<string>;

  @Field(type => [CampusLessonDiagnostic], {
    default: v => v ?? [],
  })
  public dependingOnLesson!: CampusLessonDiagnostic[];

  @Field(type => Int, {
    resolve: function (this: CampusLessonDiagnostic) {
      return async () => {
        const lesson = await loadMongoData(CampusLesson, this.lessonID ?? '', {
          projection: { exerciseIDs: 1 },
        });
        const exercices = await loadMongoData(CampusExercise, lesson?.exerciseIDs ?? [], {
          projection: { type: 1 },
        });

        return exercices.reduce((acc, ex) => {
          if ([ExerciseType.H5P].includes(ex.type)) {
            return acc + 1;
          }
          return acc;
        }, 0);
      };
    },
  })
  public scoreableExercices: Maybe<number>;

  @Field(type => Int, {
    resolve: function (this: CampusLessonDiagnostic) {
      return async () => {
        return getCorrectAnswerForLesson(this.userID ?? '', this.lessonID ?? '')();
      };
    },
  })
  public correctExercices: Maybe<number>;

  @Field(type => [Float], {
    resolve: function (this: CampusLessonDiagnostic) {
      return async () => {
        return getPartialScoresForLesson(this.userID ?? '', this.lessonID ?? '')();
      };
    },
  })
  public partialScores: Maybe<number[]>;

  get correctRatio() {
    if (!this.scoreableExercices || !this.correctExercices) {
      return 0;
    }

    return this.correctExercices / this.scoreableExercices;
  }

  @Field(type => Float, {
    default: v => v ?? 0.0,
  })
  public ratio!: number;
}

const resolveGetDiagnosticForLessons = (userID: string, lessonIDs: string[]) => {
  return async (gqlFields: BaseQLSelectionSet<CampusLessonDiagnostic>) => {
    if (!lessonIDs.length) return [];
    if (!userID.length) throw new Error('Invalid userID');
    const lessonsCol = await getRawCollection<CampusLesson>(CampusLesson._collectionName);
    const lessons = await lessonsCol.find<CampusLesson>({ _id: { $in: lessonIDs } }).toArray();
    if (!lessons.length || lessons.length !== lessonIDs.length) {
      throw new Error('lessonIDs contains invalid lesson ids');
    }

    const eventlogLookupOnLessons = {
      $lookup: {
        from: 'eventlog',
        let: { initialLessonID: '$_id' },
        pipeline: [
          {
            $match: {
              $expr: {
                $and: [{ $eq: ['$userID', userID] }, { $eq: ['$lessonID', '$$initialLessonID'] }],
              },
            },
          },
          {
            $sort: {
              createdAt: -1,
            },
          },
          {
            $group: { _id: { lessonID: '$lessonID', contentID: '$contentID' } },
          },
        ],
        as: 'eventlogs',
      },
    };

    const calculateLessonRatio = {
      $project: {
        _id: 1,
        _class: 'CampusLessonDiagnostic',
        name: 1,
        lessonID: '$_id',
        dependingOnLesson: '$lessons',
        userID: userID,
        ratio: {
          $cond: {
            if: { $gt: [{ $size: '$exerciseIDs' }, 0] },
            then: { $divide: [{ $size: '$eventlogs' }, { $size: '$exerciseIDs' }] },
            else: 0,
          },
        },
      },
    };

    const result = await lessonsCol
      .aggregate([
        {
          $match: {
            _id: { $in: lessonIDs },
          },
        },
        {
          $lookup: {
            from: 'campus-lessons',
            let: { lessonIDsDependingOn: '$lessonIDsDependingOn' },
            pipeline: [
              {
                $match: {
                  $expr: {
                    $in: ['$_id', '$$lessonIDsDependingOn'],
                  },
                },
              },
              eventlogLookupOnLessons,
              calculateLessonRatio,
            ],
            as: 'lessons',
          },
        },
        eventlogLookupOnLessons,
        calculateLessonRatio,
      ])
      .toArray();

    return result;
  };
};

const queryGetDiagnosticForLessons = registerQuery({
  type: () => [CampusLessonDiagnostic],
  operationName: 'getDiagnosticForLessons',
  params: [
    { type: () => String, name: 'userID' },
    { type: () => [String], name: 'lessonIDs' },
  ],
  resolve: resolveGetDiagnosticForLessons,
});

const resolveGetSubjectsWithDiagnostics = (userID: string) => {
  return async () => {
    const user = await CurrentUserInfo.getMyself<ChancenwerkUser>()({
      roles: { name: 1, permissions: 1, contexts: 1 },
    });

    if (!user) {
      throw new Error('User not found');
    }

    // Find all matching eventlogs for the user
    const eventlogs = await getRawCollection<ContentResultEventlog>(ContentResultEventlog._collectionName);
    const matchingEventlogs = await eventlogs
      .aggregate([
        {
          $match: {
            $and: [{ userID }, { contentID: { $exists: true }, lessonID: { $exists: true } }],
          },
        },
        { $group: { _id: '$lessonID' } },
      ])
      .toArray();

    const allLessonIDs = matchingEventlogs.map(e => e._id) as string[];

    const subjects = new Set<string>();
    for (const lessonID of allLessonIDs) {
      const res = await LearningContentAPI.getSectionForLesson(lessonID)({ subjects: 1 });
      if (res) {
        for (const subject of res.subjects) {
          subjects.add(subject);
        }
      }
    }

    return Array.from(subjects);
  };
};

const queryGetSubjectsWithDiagnostics = registerQuery({
  type: () => [String],
  operationName: 'getSubjectsWithDiagnostics',
  params: [{ type: () => String, name: 'userID' }],
  resolve: resolveGetSubjectsWithDiagnostics,
});

const resolveGetSolvedExercisesForLesson = (lessonID: string) => {
  return async () => {
    const user = await CurrentUserInfo.getMyself<ChancenwerkUser>()({
      roles: { name: 1, permissions: 1, contexts: 1 },
    });

    if (!user) {
      throw new Error('User not found');
    }

    const colContentResults = await getRawCollection<ContentResultEventlog>(ContentResultEventlog._collectionName);
    const resContentResults = await colContentResults
      .aggregate([
        {
          $match: {
            _class: ContentResultEventlog._class,
            lessonID,
            userID: user._id,
          },
        },

        {
          $group: {
            _id: '$contentID',
          },
        },
      ])
      .toArray();

    const colXAPI = await getRawCollection<XAPIEventlog>(XAPIEventlog._collectionName);
    const resXAPI = await colXAPI
      .aggregate([
        {
          $match: {
            _class: XAPIEventlog._class,
            lessonID,
            userID: user._id,
            'XApiResult.completion': true,
          },
        },

        {
          $group: {
            _id: '$contentID',
          },
        },
      ])
      .toArray();

    return [...resContentResults.map(x => x._id), ...resXAPI.map(x => x._id)];
  };
};

export namespace LearningDataAPI {
  export const subscribeLessonsByFilter = subscriptionSubscribeLessonsByFilter;
  export const subscribeLessonsForArea = subscriptionSubscribeLessonsForArea;
  export const subscribeLessonWithAnswers = subscribeLessonsWithAnswerForStudent;
  export const getExercisesWithAnswers = getExercisesWithAnswerForLessonAndUser;
  export const getDiagnosticForLessons = queryGetDiagnosticForLessons;
  export const exportWithFilter = exportLearningDataWithFilter;
  export const exportMappingWithFilter = exportLearningDataMappingWithFilter;
  export const getSubjectsWithDiagnostics = queryGetSubjectsWithDiagnostics;
  export const getSolvedExercisesForLesson = registerQuery({
    type: () => [String],
    operationName: 'getSolvedExercisesForLesson',
    params: [{ type: () => String, name: 'lessonID' }],
    resolve: resolveGetSolvedExercisesForLesson,
  });
  export const setCornerFinished = registerMutation({
    type: () => [String],
    operationName: 'setCornerFinished',
    params: [{ type: () => String, name: 'lessonID' }],
    resolve: (lessonID: string) => {
      return async () => {
        const user = await CurrentUserInfo.getMyself<ChancenwerkUser>()({
          _id: 1,
          roles: { name: 1, permissions: 1, contexts: 1 },
        });

        if (!user) {
          throw new Error('User not found');
        }

        await new ChancencornerLessonFinishedEventlog({
          userID: user._id,
          lessonID,
        }).create();
      };
    },
  });
}

if (A.isServer) {
  const eventlogs = getRawCollection<any>(ChancenEventlog._collectionName);

  // Create DB Indices
  void eventlogs.then(async db => {
    await db.createIndex({ contentID: 1 });
    await db.createIndex({ lessonID: 1 });
  });
}
